import { z } from 'zod';
import { hiddenFromAgent, limitedForAgent } from './agentHidden.js';
import { addressSchema } from './common.js';
import { deliveryMethodSchema } from './customerDelivery.js';
import { classifyEmailRecipients } from './email.js';
export * from './agentConversation.js';
export * from './aiProposals.js';
export * from './apIntake.js';
export * from './arSendRun.js';
export * from './budgetPlanning.js';
export * from './budgetPlanningLoans.js';
export * from './calendar.js';
export * from './common.js';
export * from './contracts.js';
export * from './dashboard.js';
export * from './dateString.js';
export * from './displayId.js';
export * from './distributionBcc.js';
export * from './distributionLetter.js';
export * from './distributionMergeFields.js';
export * from './distributionSelection.js';
export * from './documentDistribution.js';
export * from './incomeFrequency.js';
export * from './letterTokens.js';
export * from './loans.js';
export * from './defined.js';
export * from './disputes.js';
export * from './documents.js';
export * from './email.js';
export * from './invoiceMessage.js';
export * from './invoiceReview.js';
export * from './financialReports.js';
export * from './fip.js';
export * from './financialSummary.js';
export * from './payments/paymentSchedule.js';
export * from './invoices.js';
export * from './onboarding.js';
export * from './setupBlock.js';
export * from './ownerHandoff.js';
export * from './oneTimeInvoice.js';
export * from './payabliBillStatus.js';
export * from './payments/payments.js';
export * from './expenseForecast.js';
export * from './expenses/index.js';
export * from './incomeCommitments.js';
export * from './incomeCommitmentTerm.js';
export * from './incomeProjection.js';
export * from './rentalRent.js';
export * from './payments/canonical.js';
export * from './permissions.js';
export * from './pnlSection.js';
export * from './printShop.js';
export * from './qboFieldLimits.js';
export * from './recurringInvoice.js';
export * from './rentRadar.js';
export * from './rentRadarComps.js';
export * from './rentRadarFeatures.js';
export * from './rentRadarInputs.js';
export * from './rentRadarMarket.js';
export * from './rentRoll.js';
export * from './reportPeriod.js';
export * from './reportValueColumn.js';
export * from './rfp.js';
export * from './secret.js';
export * from './signature.js';
export * from './vendors.js';
export * from './version.js';
export * from './versionData.js';
export const accountTypeSchema = z.enum(['payable', 'receivable']);
export const paymentsBoardingStatusSchema = z.enum([
    'not_started',
    'pending',
    'in_review',
    'active',
    'rejected',
]);
export const paymentConnectionOriginSchema = z.enum(['boarded', 'attached']);
export const arSetupStatusSchema = z.enum([
    'not_started',
    'draft',
    'syncing_customers',
    'customers_synced',
    'posting_invoices',
    'invoices_posted',
    'live',
]);
export const addressWithCoordinatesSchema = addressSchema.extend({
    coordinates: z
        .object({
        lat: z.number().describe('The latitude in degrees.'),
        lng: z.number().describe('The longitude in degrees.'),
    })
        .optional()
        .describe('Where the address is on the map. Omit it if you do not know it.'),
});
const hoaAssociationSchema = z.object({
    type: z.literal('hoa'),
    subtype: z
        .enum(['sfh', 'townhome', 'mixed', '55plus', 'highrise', 'midlow'])
        .describe('The kind of homeowners association.'),
});
const coaAssociationSchema = z.object({
    type: z.literal('coa'),
    subtype: z
        .enum(['highrise', 'midlow', 'mixeduse', 'condotel'])
        .describe('The kind of condominium association.'),
});
const coopAssociationSchema = z.object({
    type: z.literal('coop'),
    subtype: z
        .enum(['market', 'limited', 'landlease', 'leasehold'])
        .describe('The kind of cooperative.'),
});
export const associationTypeSchema = z.discriminatedUnion('type', [
    hoaAssociationSchema,
    coaAssociationSchema,
    coopAssociationSchema,
]);
export const authProviderSchema = z.enum(['password', 'google']);
export const agentTaskOutputStatusSchema = z.enum(['pending', 'completed', 'failed']);
export const llmCategorizationStateSchema = z.enum(['pending', 'completed', 'failed', 'mismatch']);
export const llmChatMessageSchema = z.object({
    role: z.enum(['user', 'assistant']),
    content: z.string(),
});
export const financialReportingFrequencySchema = z.enum(['never', 'daily', 'weekly', 'monthly']);
export const llmFailureOutputSchema = z.object({
    error: z.string(),
    errorType: z.string(),
    timestamp: z.string(),
});
export const llmOutputSchema = z.discriminatedUnion('status', [
    z.object({
        taskName: z.string(),
        status: z.literal('completed'),
        version: z.number().int().positive(),
        output: z.unknown(),
    }),
    z.object({
        taskName: z.string(),
        status: z.literal('failed'),
        version: z.number().int().positive(),
        output: llmFailureOutputSchema,
    }),
    z.object({
        taskName: z.string(),
        status: z.literal('mismatch'),
        version: z.number().int().positive(),
        output: z.object({
            uploadSource: z.string(),
            classifiedCategory: z.string(),
            classifiedSubcategory: z.string(),
            message: z.string(),
        }),
    }),
]);
export const notificationTypeSchema = z.enum([
    'invite_accepted',
    'property_added',
    'permission_granted',
    'document_categorized',
    'approval_requested',
    'invoice_auto_matched',
    'invoice_validation_flags',
]);
export function isNotificationType(value) {
    return notificationTypeSchema.safeParse(value).success;
}
export const ownershipProfileTypeSchema = z.enum(['llc', 'trust', 'individual', 'association']);
export function isRentalOwnershipType(type) {
    return type !== 'association';
}
export const RENT_RADAR_ASSOCIATION_MESSAGE = 'Rent Increase Radar is for rental properties. This property belongs to an association.';
export const ownershipRoleSchema = z.enum([
    'owner',
    'admin',
    'property_manager',
    'agent',
    'board_member',
]);
function sumOfValues(obj) {
    return Object.values(obj).reduce((a, b) => a + b, 0);
}
export const ownershipAllocationDeltaSchema = z.record(z.string(), z.number()).refine((data) => {
    const bounded = Object.values(data).reduce((bounded, delta) => bounded && delta >= -100 && delta < 100, true);
    const sum = sumOfValues(data);
    return bounded && sum <= 0 && sum >= -100;
}, {
    message: 'Total change must be in range [-100, 0], while individual changes must be in range [-100, 100)',
});
export const ownershipSharesSchema = z
    .object({
    current: z.record(z.string(), z.number()),
    invited: z.record(z.string(), ownershipAllocationDeltaSchema),
})
    .refine((data) => {
    const sum = sumOfValues(data.current) -
        Object.values(data.invited).reduce((a, allocationData) => a + sumOfValues(allocationData), 0);
    const bounded = Object.values(data.current).reduce((bounded, allocationData) => bounded && allocationData <= 100 && allocationData >= 0, true);
    return sum == 100 && bounded;
}, {
    path: ['current', 'invited'],
    message: 'Sum of percentages must be 100 and all percentages must be in range [0, 100]',
});
export const ownerTypeSchema = z.enum(['individual', 'llc', 'trust', 'association']);
export const portfolioRoleSchema = z.enum(['owner', 'admin', 'property_manager', 'agent']);
export const portfolioTypeSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('commercial'),
        subtype: z
            .enum(['office', 'retail', 'industrial', 'mf_commercial', 'hospitality'])
            .describe('The kind of commercial property.'),
    }),
    hoaAssociationSchema,
    coaAssociationSchema,
    coopAssociationSchema,
    z.object({
        type: z.literal('rental'),
        subtype: z
            .enum(['sfr', '2to4', '5plus', 'str'])
            .describe('The kind of rental: sfr single-family, 2to4 or 5plus units in a building, str short-term.'),
    }),
    z.object({
        type: z.literal('private'),
    }),
]);
export const profileTypeSchema = z.enum([
    'owner',
    'renter',
    'hoa_coa_board_member',
    'property_manager',
    'developer',
    'agent',
    'admin',
]);
export const boardRosterStatusSchema = z.enum(['draft', 'scheduled', 'active', 'archived']);
export const mfaRequirementSchema = z.enum(['required', 'not_required']);
export const mfaTierSchema = z.enum(['not_required', 'required', 'fiduciary']);
export const ADMIN_ACCESS_REQUIRED = 'Admin access required';
export const mfaOverrideSchema = z.object({
    kind: z.enum(['require', 'exempt']),
    note: z.string().min(1).max(280),
    setByUserId: z.number().int(),
    setAt: z.string().datetime(),
});
export const mfaSyncedTierSchema = z.object({
    tier: mfaTierSchema,
    reason: z.string().min(1).max(280),
    syncedAt: z.string().datetime(),
});
export const mfaPolicyStateSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('unsynced'),
        override: mfaOverrideSchema.optional(),
    }),
    z.object({
        status: z.literal('synced'),
        override: mfaOverrideSchema.optional(),
        syncedAs: mfaSyncedTierSchema,
    }),
]);
export const UNSYNCED_MFA_POLICY = { status: 'unsynced' };
export const idleTimeoutSchema = z.enum(['10_minutes', '30_minutes', '60_minutes', 'never']);
export const DEFAULT_IDLE_TIMEOUT = '10_minutes';
export const IDLE_TIMEOUT_MINUTES = {
    '10_minutes': 10,
    '30_minutes': 30,
    '60_minutes': 60,
};
export const propertyTypeSchema = z
    .enum(['single_family', 'multi_family', 'apartment', 'commercial', 'condo'])
    .describe('The kind of building.');
export const unitPartyTypeSchema = z.enum(['owner', 'renter', 'manager']);
export const leaseTypeSchema = z.enum(['lease', 'sublease']);
export const leaseStatusSchema = z.enum(['pending', 'active', 'ended', 'terminated', 'eviction']);
export const externalSourceSchema = z.enum(['appfolio', 'yardi']);
export const labeledContactSchema = z.object({ label: z.string(), value: z.string() });
export const unitPartyAdditionalDataSchema = z.object({
    emergencyContact: z
        .object({
        name: z.string().optional(),
        email: z.string().optional(),
        phone: z.string().optional(),
    })
        .optional(),
    insuranceCompany: z.string().optional(),
});
export const syncDirectionSchema = z.enum(['push', 'pull']);
export const syncStatusSchema = z.enum(['success', 'pending', 'failed']);
export const taxonomyTypeSchema = z.enum(['income', 'expense']);
export const tokenStatusSchema = z.enum(['ok', 'needs_reauth']);
export const billingStatusSchema = z.enum(['draft', 'confirmed', 'live']);
export const budgetStatusSchema = z.enum(['draft', 'approved', 'active', 'revoked']);
export const monthSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Must be YYYY-MM format with valid month (01-12)');
export const dueDateRuleSchema = z.enum(['due-on-1st', 'due-on-15th']);
export function dayOfMonthFromRule(rule) {
    switch (rule) {
        case 'due-on-1st':
            return 1;
        case 'due-on-15th':
            return 15;
        default: {
            const _exhaustive = rule;
            throw new Error(`Unknown due date rule: ${_exhaustive}`);
        }
    }
}
export const rentRollDueDateRuleSchema = z.enum(['lease-start-day', ...dueDateRuleSchema.options]);
export const rentRollBillingSettingsSchema = z.object({
    goLiveMonth: monthSchema,
    dueDateRule: rentRollDueDateRuleSchema,
    issueDaysAhead: z.number().int().min(0).max(30),
});
export const rentRollSkipReasonSchema = z.enum([
    'not-synced',
    'nothing-billable',
    'already-set-up',
    'lease-ends-before-billing',
]);
export const billingPropertySetupSchema = z.object({
    status: billingStatusSchema,
    goLiveMonth: monthSchema.optional(),
    dueDateRule: dueDateRuleSchema.optional(),
    customerMemo: z.string().optional(),
    issueDaysAhead: z.number().int().min(0).max(30).optional(),
    invoiceCreationDayOfMonth: z.number().int().min(1).max(28).default(5),
    emailSendDayOfMonth: z.number().int().min(1).max(28).default(15),
    paperPrintRequired: z.boolean().default(false),
    useQboEmailEngine: z.boolean().default(true),
});
export const billingSetupSchema = z.record(z.string(), billingPropertySetupSchema);
export const TEST_ONLY = {};
export function ownerEmailForAgent(schema) {
    return limitedForAgent(schema, 'Send one address. Every address after the first gets a copy of each invoice, so only a person adds one.', (value) => typeof value === 'string' && classifyEmailRecipients(value).valid.length > 1);
}
export const unitOwnerSchema = z.object({
    names: z.string().optional().describe('The owner names, as one line.'),
    emails: ownerEmailForAgent(z
        .string()
        .optional()
        .describe('The owner email: one address. Every address after the first gets a copy of each invoice.')),
    invoiceEmailCc: hiddenFromAgent(z
        .string()
        .optional()
        .describe('More addresses that get a copy of each invoice, separated by commas.'), 'Every address in it gets a copy of each invoice, so only a person adds one.'),
    phones: z.string().optional().describe('The owner primary phone: one number.'),
    alternatePhone: z.string().optional().describe('The owner second phone: one number.'),
    physicalAddress: z.string().optional().describe('Where the owner lives, as one line.'),
    mailingAddress1: z.string().optional().describe('The first line of the owner mailing address.'),
    mailingAddress2: z.string().optional().describe('The second line of the owner mailing address.'),
    mailingCity: z.string().optional().describe('The city of the owner mailing address.'),
    mailingState: z.string().optional().describe('The state of the owner mailing address.'),
    mailingZip: z.string().optional().describe('The ZIP code of the owner mailing address.'),
    mailingCountry: z.string().optional().describe('The country of the owner mailing address.'),
    taxId: z.string().optional().describe('The owner tax id.'),
    communicationMethod: deliveryMethodSchema
        .optional()
        .describe('How the owner gets invoices and notices.'),
});
const unitRenterSchema = z.object({
    names: z.string().optional().describe('The renter names, as one line.'),
    emails: z.string().optional().describe('The renter emails, separated by commas.'),
    phones: z.string().optional().describe('The renter phones, separated by commas.'),
    startDate: z.string().optional().describe('The day the renter moved in, as YYYY-MM-DD.'),
    endDate: z.string().optional().describe('The day the renter moves out, as YYYY-MM-DD.'),
    insurance: z.boolean().optional().describe('Whether the renter has renter insurance.'),
    insuranceCompany: z.string().optional().describe('The company of the renter insurance.'),
    pets: z.boolean().optional().describe('Whether the renter has pets.'),
    petsDetails: z.string().optional().describe('What pets the renter has.'),
});
const unitPropertyManagerSchema = z.object({
    names: z.string().optional().describe('The property manager names, as one line.'),
    emails: z.string().optional().describe('The property manager emails, separated by commas.'),
    phones: z.string().optional().describe('The property manager phones, separated by commas.'),
    company: z.string().optional().describe('The property management company.'),
    website: z.string().optional().describe('The website of the property management company.'),
});
export function ownerRecipientEmails(owner) {
    const parts = [owner?.emails, owner?.invoiceEmailCc]
        .map((value) => value?.trim())
        .filter((value) => value !== undefined && value !== '');
    return parts.length > 0 ? parts.join(', ') : undefined;
}
export function sameOwnerName(a, b) {
    return (a ?? '').trim().toLocaleLowerCase() === (b ?? '').trim().toLocaleLowerCase();
}
export const unitAssessmentSchema = z.object({
    period: z.string().optional().describe('The billing period that the amounts are for.'),
    commonCharge: z.number().optional().describe('The common charge in dollars.'),
    commonChargeLate: z.number().optional().describe('The late fee on the common charge in dollars.'),
    specialAssessment1: z.number().optional().describe('The first special assessment in dollars.'),
    specialAssessment2: z.number().optional().describe('The second special assessment in dollars.'),
    specialAssessment3: z.number().optional().describe('The third special assessment in dollars.'),
    specialAssessment4: z.number().optional().describe('The fourth special assessment in dollars.'),
    specialAssessmentLate: z
        .number()
        .optional()
        .describe('The late fee on the special assessments in dollars.'),
    parking: z.number().optional().describe('The parking charge in dollars.'),
    others: z.number().optional().describe('Other charges in dollars.'),
    finesOrAdjustments: z.number().optional().describe('Fines and adjustments in dollars.'),
    currentBalance: z
        .number()
        .optional()
        .describe('What the owner owes now, in dollars. It is the opening balance that goes to the accounting system.'),
});
export const unitAdditionalDataSchema = z.object({
    parcelId: z.string().optional().describe('The parcel id from the county records.'),
    homestead: z.boolean().optional().describe('Whether the unit has a homestead exemption.'),
    owner: unitOwnerSchema.optional().describe('The owner of the unit.'),
    renter: unitRenterSchema
        .optional()
        .describe('The renter of the unit. Send every renter field, because this part is replaced.'),
    propertyManager: unitPropertyManagerSchema
        .optional()
        .describe('The property manager of the unit. Send every field, because this part is replaced.'),
    assessment: hiddenFromAgent(unitAssessmentSchema
        .optional()
        .describe('The association charges of the unit. Send every field, because this part is replaced.'), 'The owner is billed from the assessment. A fine or a one-time charge is a one-time invoice, which the person creates in TEOAI.'),
});
export * from './agentHidden.js';
export * from './dollarsToCents.js';
export * from './budgetEngine/index.js';
export * from './customerDelivery.js';
export * from './budgetApproval.js';
export * from './setupBlock.js';
//# sourceMappingURL=index.js.map